import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Tooltip,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  WbSunny as TomorrowIcon,
  Restaurant as RestaurantIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ArrowForward as CarryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { executiveAssignmentsApi, rhythmApi, authApi, workItemsApi, calendarApi } from '../api/services';
import type { ExecutiveAssignment, StartMyDayResponse, RestaurantResult, WorkItem, CalendarEvent } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
}

interface ExecutiveSummary {
  carryForwardCount: number;
  nextMeeting: string | null; // title of next meeting
  nextMeetingTime: string | null;
  capacityRemainingHours: number;
  isOverloaded: boolean;
  overflowItems: WorkItem[];
}

interface ExecutiveState {
  assignment: ExecutiveAssignment;
  user: UserInfo | null;
  dayData: StartMyDayResponse | null;
  summary: ExecutiveSummary | null;
  loading: boolean;
  error: string | null;
}

export default function EAWorkspacePage() {
  const navigate = useNavigate();
  const [executives, setExecutives] = useState<ExecutiveState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantResult | null>(null);
  const today = format(new Date(), 'yyyy-MM-dd');

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setSelectedRestaurant(restaurant);
    // Navigate to calendar page with restaurant info as a simple alert for now
    window.location.href = `/calendar`;
  };

  // Load summary data for an executive
  const loadExecSummary = async (execUserId: string): Promise<ExecutiveSummary | null> => {
    try {
      const [todayItems, calEvents] = await Promise.all([
        workItemsApi.getByDate(today).catch(() => [] as WorkItem[]),
        calendarApi.getByDate(today).catch(() => [] as CalendarEvent[]),
      ]);

      const carryForwardCount = todayItems.filter(i => i.isCarryForward).length;
      const now = new Date();
      const upcomingMeetings = calEvents
        .filter(e => !e.isAllDay && new Date(e.startTime) > now)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      const nextMeeting = upcomingMeetings.length > 0 ? upcomingMeetings[0].title : null;
      const nextMeetingTime = upcomingMeetings.length > 0 ? upcomingMeetings[0].startTime : null;

      const meetingMinutes = calEvents
        .filter(e => !e.isAllDay)
        .reduce((sum, e) => {
          const start = new Date(e.startTime);
          const end = new Date(e.endTime);
          return sum + Math.max(0, (end.getTime() - start.getTime()) / 60000);
        }, 0);
      const plannedMinutes = todayItems
        .filter(i => !['Done', 'Cancelled'].includes(i.status))
        .reduce((sum, i) => sum + (i.estimatedMinutes || 0), 0);
      const totalWorkingMinutes = 8 * 60;
      const capacityRemainingHours = Math.max(0, Math.round((totalWorkingMinutes - meetingMinutes - plannedMinutes) / 60 * 10) / 10);
      const isOverloaded = (meetingMinutes + plannedMinutes) > totalWorkingMinutes;
      const overflowItems = isOverloaded
        ? todayItems.filter(i => !['Done', 'Cancelled'].includes(i.status)).slice(-3)
        : [];

      return { carryForwardCount, nextMeeting, nextMeetingTime, capacityRemainingHours, isOverloaded, overflowItems };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [assignments, usersData] = await Promise.all([
          executiveAssignmentsApi.getAll(),
          authApi.getUsers(),
        ]);
        setUsers(usersData);
        const activeAssignments = assignments.filter(a => a.isActive);
        const execStates: ExecutiveState[] = activeAssignments.map(a => ({
          assignment: a,
          user: usersData.find(u => u.id === a.executiveUserId) || null,
          dayData: null,
          summary: null,
          loading: false,
          error: null,
        }));
        setExecutives(execStates);

        // Load summaries in parallel
        const summaries = await Promise.all(
          activeAssignments.map(a => loadExecSummary(a.executiveUserId))
        );
        setExecutives(prev => prev.map((e, i) => ({ ...e, summary: summaries[i] })));
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load assignments');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (index: number, action: 'start' | 'replan' | 'planTomorrow') => {
    setExecutives(prev => prev.map((e, i) => i === index ? { ...e, loading: true, error: null } : e));
    try {
      const execUserId = executives[index].assignment.executiveUserId;
      let data: StartMyDayResponse;
      if (action === 'start') {
        data = await rhythmApi.startMyDay({ forExecutiveUserId: execUserId } as any);
      } else if (action === 'replan') {
        data = await rhythmApi.replan({ forExecutiveUserId: execUserId } as any);
      } else {
        data = await rhythmApi.planTomorrow({ forExecutiveUserId: execUserId } as any);
      }
      setExecutives(prev => prev.map((e, i) => i === index ? { ...e, dayData: data, loading: false } : e));
    } catch (err: any) {
      setExecutives(prev => prev.map((e, i) => i === index
        ? { ...e, loading: false, error: err.response?.data?.message || err.message || 'Action failed' }
        : e
      ));
    }
  };

  const formatMeetingTime = (iso: string): string => {
    try {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <GroupsIcon sx={{ color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">EA Workspace</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your assigned executives
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && executives.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>No executive assignments found</Typography>
          <Typography color="text.secondary">
            Contact your admin to get assigned to an executive.
          </Typography>
        </Card>
      )}

      <Grid container spacing={2.5}>
        {executives.map((exec, index) => (
          <Grid size={{ xs: 12 }} key={exec.assignment.id}>
            <Card>
              <CardContent>
                {/* Executive Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6">
                      {exec.user?.displayName || exec.assignment.executiveUserId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exec.user?.email || 'Executive'}
                      {exec.user && (
                        <Chip label={exec.user.role} size="small" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    label={exec.assignment.isActive ? 'Active' : 'Inactive'}
                    color={exec.assignment.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-executive summary strip */}
                {exec.summary && (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                      label={exec.summary.nextMeeting
                        ? `Next: ${exec.summary.nextMeeting} at ${formatMeetingTime(exec.summary.nextMeetingTime!)}`
                        : 'No upcoming meetings'}
                      size="small"
                      variant="outlined"
                    />
                    {exec.summary.carryForwardCount > 0 && (
                      <Chip
                        icon={<CarryIcon sx={{ fontSize: 14 }} />}
                        label={`${exec.summary.carryForwardCount} carried forward`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={`${exec.summary.capacityRemainingHours}h remaining`}
                      size="small"
                      color={exec.summary.isOverloaded ? 'error' : 'success'}
                      variant="outlined"
                    />
                    {exec.dayData && (
                      <Chip
                        label={`Plan: ${exec.dayData.plan.status}`}
                        size="small"
                        color={exec.dayData.plan.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}

                {/* Quick Actions */}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Quick Actions</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<StartIcon />}
                    onClick={() => handleAction(index, 'start')}
                    disabled={exec.loading}
                  >
                    Start Their Day
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplanIcon />}
                    onClick={() => handleAction(index, 'replan')}
                    disabled={exec.loading}
                  >
                    Replan
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TomorrowIcon />}
                    onClick={() => handleAction(index, 'planTomorrow')}
                    disabled={exec.loading}
                  >
                    Plan Tomorrow
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/principal-profile/${exec.assignment.executiveUserId}`)}
                  >
                    Principal Profile
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestaurantIcon />}
                    onClick={() => setRestaurantDialogOpen(true)}
                  >
                    Book Restaurant
                  </Button>
                </Stack>

                {exec.loading && <LinearProgress sx={{ mb: 2 }} />}
                {exec.error && <Alert severity="error" sx={{ mb: 2 }}>{exec.error}</Alert>}

                {/* Today's Overview */}
                {exec.dayData && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Today's Overview</Typography>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Available</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.availableMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Meetings</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.meetingMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Planned</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.plannedMinutes / 60 * 10) / 10}h
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" color="text.secondary">Utilisation</Typography>
                          <Typography variant="h6">
                            {Math.round(exec.dayData.capacity.utilisationPercent)}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        label={`Plan: ${exec.dayData.plan.status}`}
                        size="small"
                        color={exec.dayData.plan.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      {exec.dayData.plan.isOverloaded && (
                        <Chip label="Overloaded" size="small" color="error" variant="outlined" />
                      )}
                      {exec.dayData.warnings.length > 0 && (
                        <Chip label={`${exec.dayData.warnings.length} warnings`} size="small" color="warning" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Box>

                    {/* Overflow Triage — only when overloaded */}
                    {exec.dayData.plan.isOverloaded && exec.dayData.overflowItems.length > 0 && (
                      <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: 1, borderColor: 'error.main', bgcolor: 'rgba(244,67,54,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          <Typography variant="subtitle2" color="error.main">
                            Overflow Triage ({exec.dayData.overflowItems.length} items)
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          This executive's day is overloaded. The following items exceed available capacity:
                        </Typography>
                        <Stack spacing={0.5}>
                          {exec.dayData.overflowItems.map(item => (
                            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <Chip label={item.priority} size="small" variant="outlined"
                                color={item.priority === 'Critical' ? 'error' : item.priority === 'High' ? 'warning' : 'default'}
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>{item.title}</Typography>
                              <Typography variant="caption" color="text.secondary">{item.estimatedMinutes}m</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Restaurant Search Dialog */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />
    </Box>
  );
}
